
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const useRealTimeStats = () => {
  return useQuery({
    queryKey: ["real-time-stats"],
    queryFn: async () => {
      // استخراج الإحصائيات الأساسية
      const [
        employeesResult,
        clientsResult,
        companiesResult,
        bookingsResult,
        tasksResult,
        hotelsResult
      ] = await Promise.all([
        // الموظفين مع حالات الحضور
        supabase
          .from("employees")
          .select("id, name, status, department, position, last_seen")
          .order("last_seen", { ascending: false }),
        
        // العملاء مع الشركات
        supabase
          .from("clients")
          .select(`
            id, name_ar, status, created_at,
            companies:company_id (name_ar)
          `),
        
        // الشركات
        supabase
          .from("companies")
          .select("id, name_ar")
          .order("created_at", { ascending: false }),
        
        // الحجوزات مع تفاصيل إضافية
        supabase
          .from("package_bookings")
          .select(`
            id, price, status, created_at, booking_date,
            clients!inner(name_ar, status),
            employees(name, department)
          `)
          .order("created_at", { ascending: false }),
        
        // المهام النشطة
        supabase
          .from("tasks")
          .select(`
            id, title, status, priority, due_date, created_at,
            assigned_to, created_by
          `)
          .in("status", ["pending", "in_progress"])
          .order("created_at", { ascending: false }),
        
        // الفنادق
        supabase
          .from("hotels")
          .select("id, name_ar, country, city, stars")
      ]);

      // التحقق من الأخطاء
      if (employeesResult.error) throw employeesResult.error;
      if (clientsResult.error) throw clientsResult.error;
      if (companiesResult.error) throw companiesResult.error;
      if (bookingsResult.error) throw bookingsResult.error;
      if (tasksResult.error) throw tasksResult.error;
      if (hotelsResult.error) throw hotelsResult.error;

      const employees = employeesResult.data || [];
      const clients = clientsResult.data || [];
      const companies = companiesResult.data || [];
      const bookings = bookingsResult.data || [];
      const tasks = tasksResult.data || [];
      const hotels = hotelsResult.data || [];

      // حساب إحصائيات الموظفين
      const employeeStats = {
        total: employees.length,
        present: employees.filter(emp => emp.status === 'present').length,
        absent: employees.filter(emp => emp.status === 'absent').length,
        late: employees.filter(emp => emp.status === 'late').length,
        departments: [...new Set(employees.map(emp => emp.department))].length,
        attendanceRate: 0 // سيتم حسابه بعد ذلك
      };

      // حساب معدل الحضور
      employeeStats.attendanceRate = employeeStats.total > 0 
        ? Math.round((employeeStats.present / employeeStats.total) * 100) 
        : 0;

      // إحصائيات العملاء
      const clientStats = {
        total: clients.length,
        byStatus: clients.reduce((acc: any, client) => {
          const status = client.status || 'غير محدد';
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        }, {}),
        recentClients: clients.slice(0, 5)
      };

      // إحصائيات الحجوزات
      const today = new Date();
      const thisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      
      const bookingStats = {
        total: bookings.length,
        thisMonth: bookings.filter(booking => 
          new Date(booking.created_at) >= thisMonth
        ).length,
        totalRevenue: bookings.reduce((sum, booking) => 
          sum + (parseFloat(String(booking.price)) || 0), 0
        ),
        byStatus: bookings.reduce((acc: any, booking) => {
          acc[booking.status] = (acc[booking.status] || 0) + 1;
          return acc;
        }, {}),
        recentBookings: bookings.slice(0, 5)
      };

      // إحصائيات المهام
      const taskStats = {
        total: tasks.length,
        pending: tasks.filter(task => task.status === 'pending').length,
        inProgress: tasks.filter(task => task.status === 'in_progress').length,
        overdue: tasks.filter(task => 
          task.due_date && new Date(task.due_date) < today
        ).length,
        urgent: tasks.filter(task => task.priority === 'urgent').length
      };

      // إحصائيات الفنادق
      const hotelStats = {
        total: hotels.length,
        countries: [...new Set(hotels.map(hotel => hotel.country))].length,
        cities: [...new Set(hotels.map(hotel => hotel.city))].length,
        averageStars: hotels.length > 0 
          ? hotels.reduce((sum, hotel) => sum + hotel.stars, 0) / hotels.length 
          : 0
      };

      return {
        employees: employeeStats,
        clients: clientStats,
        companies: { total: companies.length },
        bookings: bookingStats,
        tasks: taskStats,
        hotels: hotelStats,
        lastUpdated: new Date().toISOString()
      };
    },
    staleTime: 30000, // 30 ثانية
    gcTime: 60000, // دقيقة واحدة
    refetchOnWindowFocus: false,
    refetchInterval: 60000 // تحديث كل دقيقة
  });
};
